/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {alpha} from '@material-ui/core/styles';
import {getAssetState, getBatteryVoltageFixed} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import Device from '@ion/core/src/services/Device';
import {AssetState} from '@ion/core/src/types/entities';
import {getEventSpeed} from '@ion/core/src/units/helpers';
import {isNonZeroNumber} from '@ion/core/src/utils/NumberUtil';
import {padDelimiterLeft} from '@ion/core/src/utils/StringUtil';
import {AlertIndicatorValues} from '@ion/dls/src/components/assetcard/current/AssetAlertIndicators';
import {HealthIndicatorValues} from '@ion/dls/src/components/assetcard/current/AssetHealthIndicators';
import {AssetSummaryCardProps} from '@ion/dls/src/components/assetcard/current/AssetSummaryCard';
import {AssetSummaryCardStatusProps} from '@ion/dls/src/components/assetcard/current/AssetSummaryCardStatus';
import {IndicatorStatus, VehicleFuelType} from '@ion/dls/src/components/assetcard/current/vehicleTypeIndicatorConfig';
import {StateDotIcon} from '@ion/dls/src/icons/current/StateDotIcon';
import {NoIgnitionIcon} from '@ion/dls/src/icons/NoIgnitionIcon';
import {dlsColor, dlsDarkColor, dlsTextColor} from '@ion/dls/src/theme/theme';
import {isDarkMode} from '@ion/dls/src/theme/ThemeStore';
import {formatSpeed, getFormattedDateTimeZoneParts} from '@ion/dls/src/utils/ComponentUtils';

interface SeatbeltDisplay {
  value: string;
  status: IndicatorStatus;
}

type StateTagProps = StateTagStyle & {StateIcon: AssetSummaryCardStatusProps['StateIcon']};

type StateTagStyle = Pick<AssetSummaryCardStatusProps,
'stateTextColor' | 'stateBackgroundColor' | 'stateIconColor' | 'stateDotColor'>;

/**
 * Maps a raw `fuelType` code (from the asset/device data model) to the
 * `VehicleFuelType` the Asset Summary Card's indicator config is keyed by.
 * Diesel is treated as `gas` - the ticket's Vehicle Type Indicator table has
 * no separate Diesel column, and diesel is a combustion (non-electric)
 * fuel type like gas.
 */
const getVehicleFuelType = (fuelTypeCode?: string): VehicleFuelType => {
  let fuelType: VehicleFuelType = 'gas';
  switch (fuelTypeCode?.substring(0, 1)?.toUpperCase()) {
    case 'E':
      fuelType = 'ev';
      break;
    case 'H':
      fuelType = 'hybrid';
      break;
    default:
      break;
  }
  return fuelType;
};

/**
 * Resolves a numeric value against a critical/warning cutoff pair into an
 * `IndicatorStatus` - shared by every Health indicator with a simple
 * two-cutoff banding (Device Battery, Asset Battery). `isBelowCutoff`
 * defaults to `<=` (Device Battery's percent thresholds are inclusive);
 * pass a strict `<` comparator for an exclusive band like Asset Battery's
 * voltage thresholds.
 */
const thresholdToStatus = (
  value: number,
  cutoffs: {critical: number; warning: number},
  isBelowCutoff: (value: number, cutoff: number) => boolean = (belowValue, cutoff) => belowValue <= cutoff
): IndicatorStatus => {
  let status: IndicatorStatus;
  if (isBelowCutoff(value, cutoffs.critical)) {
    status = 'critical';
  } else if (isBelowCutoff(value, cutoffs.warning)) {
    status = 'warning';
  } else {
    status = 'default';
  }
  return status;
};

/**
 * Device Battery severity thresholds per ION-7360: 51-100% Good (Grey 3),
 * 21-50% Warning (Orange), 0-20% Critical (Red).
 */
const getDeviceBatteryStatus = (percent: number): IndicatorStatus => (
  thresholdToStatus(percent, {critical: 20, warning: 50})
);

/**
 * Asset Battery (12V) severity thresholds, applied to the raw millivolt
 * `adcExternalPowerVoltage` reading: >=12.4V Good, 11.5-12.39V Warning,
 * <11.5V Critical - the standard automotive 12V lead-acid resting-voltage
 * bands. PROVISIONAL: ION-7360's cited thresholds are all percent-based and
 * don't apply to a voltage reading, so no confirmed cutoff exists for this
 * attribute yet - replace with real values if design/product specifies
 * different ones.
 */
const getAssetBatteryStatus = (millivolts: number): IndicatorStatus => (
  thresholdToStatus(millivolts, {critical: 11500, warning: 12400}, (value, cutoff) => value < cutoff)
);

/**
 * Seatbelt display text/status, keyed by the raw `seatBeltStatus` value.
 * Any raw value not in this map (e.g. the confirmed "N/A" sample, or
 * anything else unrecognized) falls back to "Unsupported". Built fresh per
 * call (not a module-scope constant) so `translate()` always resolves
 * against the currently active locale.
 */
const getSeatbeltStatusMap = (): Record<string, SeatbeltDisplay> => ({
  Parked    : {value: translate('common:assetCard.seatbelt.parked'), status: 'default'},
  Buckled   : {value: translate('common:assetCard.seatbelt.buckled'), status: 'default'},
  Unbuckled : {value: translate('common:assetCard.seatbelt.unbuckled'), status: 'critical'},
  Error     : {value: translate('common:assetCard.seatbelt.error'), status: 'warning'}
});

const getSeatbeltUnsupported = (): SeatbeltDisplay => (
  {value: translate('common:assetCard.seatbelt.unsupported'), status: 'unsupported'}
);

/**
 * Builds a DLS "Tags by Status" style from one base color - the dot uses
 * the base color and the background is that same color at 10% opacity
 * (matches the DLS hex values exactly over white). The DLS status tags have
 * no border.
 */
export const getStatusTagStyle = (baseColor: string, textColor: string): StateTagStyle => ({
  stateTextColor       : textColor,
  stateBackgroundColor : alpha(baseColor, 0.1),
  stateIconColor       : baseColor
});

/**
 * State Tag colors and icon, keyed by `AssetState`, per the DLS "Tags by
 * Status". Every state renders a `StateIcon` before its label - a colored
 * dot, except Towed ("Moving (No Ignition)" in the UI) which renders the
 * grey no-ignition glyph followed by an orange dot. `isDark` picks the Parked
 * text color; a caller that re-renders on a mode switch passes the hook value.
 */
export const getStateTagProps = (assetState: AssetState, isDark: boolean): StateTagProps => {
  const orange = dlsColor('orange');
  const stateTagStyles: Partial<Record<AssetState, StateTagStyle>> = {
    [AssetState.MOVING] : getStatusTagStyle(dlsColor('caGreen'), dlsTextColor('green')),
    [AssetState.IDLE]   : getStatusTagStyle(dlsColor('caYellow'), dlsTextColor('yellow')),
    [AssetState.NOGPS]  : getStatusTagStyle(orange, dlsTextColor('orange')),
    [AssetState.PARKED] : {
      stateTextColor       : dlsDarkColor(isDark, 'grey3', '#6E6E6E'),
      stateBackgroundColor : 'rgba(130, 130, 130, 0.1)',
      stateIconColor       : dlsColor('grey3')
    },
    [AssetState.TOWED] : {
      ...getStatusTagStyle(orange, dlsTextColor('orange')),
      stateIconColor : dlsColor('grey3'),
      stateDotColor  : orange
    }
  };
  const defaultStyle: StateTagStyle = {
    stateTextColor       : dlsColor('grey2'),
    stateBackgroundColor : dlsColor('grey6'),
    stateIconColor       : dlsColor('grey3')
  };
  return {
    ...(stateTagStyles[assetState] || defaultStyle),
    StateIcon : assetState === AssetState.TOWED ? NoIgnitionIcon : StateDotIcon
  };
};

/**
 * Maps a `Device` to the Health indicator values `AssetSummaryCard` needs -
 * seatbelt, asset battery (12V), fuel, charge, device battery. Seatbelt
 * (`seatBeltStatus`) isn't declared on the `CVSAssetType`/`Device`
 * TypeScript model yet (confirmed by search), but it IS present on the raw
 * API payload.
 */
export const getHealthIndicatorValues = (asset: Device): HealthIndicatorValues => {
  const fuelPercent    = asset.getAssetValue('vbFuelPercent');
  const assetBattery   = asset.getAssetValue('adcExternalPowerVoltage');
  const deviceBattery  = asset.getAssetValue('batteryRemainingPercent');
  const evCharge       = asset.getAssetValue('evBatteryPackRemainingCharge');
  const seatBeltStatus = asset.getCommonDeviceAttribute('seatBeltStatus');

  const healthValues: HealthIndicatorValues = {};
  // per the Figma annotation "Seatbelt always displays" - unlike the other
  // indicators, this one is populated whenever the raw attribute has any
  // value at all (including "N/A"), not gated on a numeric check ...
  if (seatBeltStatus) {
    healthValues.seatbelt = getSeatbeltStatusMap()[seatBeltStatus] ?? getSeatbeltUnsupported();
  }
  // adcExternalPowerVoltage is a raw millivolt reading (12V vehicle system
  // voltage) - `getBatteryVoltageFixed` is the same formatter the grid/detail
  // panel columns already use for this attribute (Volts, 3 decimals) ...
  if (isNonZeroNumber(assetBattery)) {
    healthValues.assetBattery = {
      value  : `${getBatteryVoltageFixed(assetBattery)} V`,
      status : getAssetBatteryStatus(assetBattery)
    };
  }
  if (isNonZeroNumber(fuelPercent)) {
    healthValues.fuel = {value: `${fuelPercent}%`};
  }
  if (isNonZeroNumber(evCharge)) {
    healthValues.charge = {value: `${evCharge}%`};
  }
  if (isNonZeroNumber(deviceBattery)) {
    healthValues.deviceBattery = {value: `${deviceBattery}%`, status: getDeviceBatteryStatus(deviceBattery)};
  }
  return healthValues;
};

/**
 * Maps a `Device` to the Alert indicator values `AssetSummaryCard` needs -
 * GPS quality and Engine Light.
 */
export const getAlertIndicatorValues = (asset: Device): AlertIndicatorValues => {
  // matches the legacy AssetCardListContent.tsx's already-established
  // distinction: no coordinates at all is Critical "No GPS"; coordinates
  // present but the gpsValidity flag stale/false is Warning "Poor" ...
  const isValidGPS   = isNonZeroNumber(asset.latitude()) && isNonZeroNumber(asset.longitude());
  const engineLightOn = asset.getAssetValue('vbusMilStatus') === 'true';

  const alertValues: AlertIndicatorValues = {};
  if (!isValidGPS) {
    alertValues.gpsQuality = {value: translate('tracking:list.detailed.noGps'), status: 'critical'};
  } else if (!asset.isGpsValid()) {
    // "Poor", not the shared `poorGpsQuality` translation's full "Poor GPS
    // Quality" - this card shows the status only, not a repeat of the
    // indicator's own label ...
    alertValues.gpsQuality = {value: translate('common:assetCard.gpsQuality.poor'), status: 'warning'};
  }
  if (engineLightOn) {
    alertValues.engineLight = {value: translate('common:assetCard.engineLight')};
  }
  return alertValues;
};

/**
 * Maps an `AssetState` and `Device` to the card's Header Right (Asset
 * Status): state label/color/icon (via `getStateTagProps`), date/time, and
 * speed - the whole Speed row is omitted (not just blank) while stationary.
 */
const getAssetStatusProps = (assetState: AssetState, asset: Device): AssetSummaryCardStatusProps => {
  const device       = asset.getDevice();
  const isStationary = assetState === AssetState.PARKED ||
    assetState === AssetState.IDLE ||
    assetState === AssetState.STATIONARY;
  const {speed, units} = getEventSpeed(device);
  const {date, time} = getFormattedDateTimeZoneParts(asset.eventTime());

  // Tool assets have no Moving/Idle/Parked concept - getAssetState() returns
  // an empty string for them (not a recognized AssetState), which would
  // otherwise translate to a garbled label rather than a clean fallback ...
  return {
    stateLabel : assetState ? translate(`common:assetState.${assetState}`) : translate('common:notAvailable'),
    ...getStateTagProps(assetState, isDarkMode()),
    dateTime   : `${date} - ${time}`.toUpperCase(),
    speed      : isStationary ? undefined : formatSpeed(speed, units)
  };
};

/**
 * Maps a `Device` to the props `AssetSummaryCard` needs to render it.
 *
 * ```typescript
 *
 *   import {getAssetSummaryCardProps} from '@/routes/tracking/getAssetSummaryCardProps';
 *
 *   const cardProps = getAssetSummaryCardProps(asset, {onAssetNameClick, onDriverClick});
 *
 * ```
 *
 */
export const getAssetSummaryCardProps = (
  asset: Device,
  handlers: {onAssetNameClick?: () => void; onDriverClick?: () => void} = {}
): AssetSummaryCardProps => {
  const device = asset.getDevice();
  const {
    city,
    state,
    street,
    routeName,
    operatorNames
  } = device;

  const assetState = getAssetState(device);
  const vehicleType = getVehicleFuelType(asset.getCommonDeviceAttribute('fuelType'));
  const isValidGPS  = isNonZeroNumber(asset.latitude()) && isNonZeroNumber(asset.longitude());

  return {
    assetType : device.assetType,
    assetName : asset.getAssetName(),
    alias     : asset.getItemValue({
      deviceAttribute : 'deviceIdValue',
      cvsAttribute    : 'assetAlias',
      isCVSAttribute  : true
    }),
    onAssetNameClick : handlers.onAssetNameClick,
    status           : getAssetStatusProps(assetState, asset),
    location         : isValidGPS
      ? [street, city, state].filter(Boolean).join(', ')
      : undefined,
    group         : asset.accountName(),
    route         : routeName || undefined,
    driver        : padDelimiterLeft(operatorNames) || undefined,
    onDriverClick : handlers.onDriverClick,
    vehicleType,
    healthValues  : getHealthIndicatorValues(asset),
    alertValues   : getAlertIndicatorValues(asset)
  };
};

export default getAssetSummaryCardProps;
